import { type DateInput, toDate } from './date';

/**
 * India-first display formatting (D-013): ₹ with Indian digit grouping (1,00,000),
 * dates as DD MMM YYYY in the workspace timezone (default Asia/Kolkata).
 *
 * Digit grouping is implemented here rather than via `Intl.NumberFormat('en-IN')` so output is
 * identical on every runtime (Node, browsers, Hermes), regardless of the ICU data shipped.
 * Timezone conversion uses `Intl.DateTimeFormat`, which every supported runtime provides.
 */

export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

// ---------------------------------------------------------------- numbers

/** Groups a string of digits the Indian way: last three digits, then pairs (12,34,56,789). */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const lastThree = digits.slice(-3);
  let rest = digits.slice(0, -3);
  const pairs: string[] = [];
  while (rest.length > 2) {
    pairs.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest.length > 0) pairs.unshift(rest);
  return `${pairs.join(',')},${lastThree}`;
}

/** Rounds half away from zero, so -1.5 → -2 and 1.5 → 2 (symmetric for refunds/credits). */
function roundHalfAwayFromZero(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

function assertFinite(value: number, fn: string): void {
  if (!Number.isFinite(value))
    throw new RangeError(`${fn}: expected a finite number, got ${value}`);
}

export interface FormatINROptions {
  /** Show paise (`₹1,00,000.00`). When false, rounds to whole rupees (`₹1,00,000`). Default true. */
  decimals?: boolean;
}

/**
 * Formats an amount of **paise** as Indian rupees: `formatINR(10000000)` → `₹1,00,000.00`.
 * Accepts `bigint` (money columns are `bigint` paise, D-012) or a number; fractional paise are
 * rounded half away from zero. Negative amounts are prefixed with a minus: `-₹500.00`.
 */
export function formatINR(paise: number | bigint, options: FormatINROptions = {}): string {
  const { decimals = true } = options;
  let amount: bigint;
  if (typeof paise === 'bigint') {
    amount = paise;
  } else {
    assertFinite(paise, 'formatINR');
    amount = BigInt(roundHalfAwayFromZero(paise));
  }
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;

  if (!decimals) {
    const rupees = (abs + 50n) / 100n;
    const sign = negative && rupees > 0n ? '-' : '';
    return `${sign}₹${groupIndian(rupees.toString())}`;
  }

  const rupees = abs / 100n;
  const rest = abs % 100n;
  const sign = negative && abs > 0n ? '-' : '';
  return `${sign}₹${groupIndian(rupees.toString())}.${rest.toString().padStart(2, '0')}`;
}

export interface FormatNumberINOptions {
  /** Default 2. Extra digits are rounded; trailing zeros are dropped down to the minimum. */
  maximumFractionDigits?: number;
  /** Default 0. */
  minimumFractionDigits?: number;
}

/**
 * Formats a number with Indian digit grouping: `formatNumberIN(100000)` → `1,00,000`,
 * `formatNumberIN(1234567.891)` → `12,34,567.89`. For display only — do money maths in paise.
 */
export function formatNumberIN(
  value: number | bigint,
  options: FormatNumberINOptions = {},
): string {
  if (typeof value === 'bigint') {
    const negative = value < 0n;
    const digits = (negative ? -value : value).toString();
    return `${negative ? '-' : ''}${groupIndian(digits)}`;
  }
  assertFinite(value, 'formatNumberIN');
  const maxFd = options.maximumFractionDigits ?? 2;
  const minFd = Math.min(options.minimumFractionDigits ?? 0, maxFd);
  if (!Number.isInteger(maxFd) || maxFd < 0 || maxFd > 20) {
    throw new RangeError('formatNumberIN: maximumFractionDigits must be an integer from 0 to 20');
  }

  const negative = value < 0;
  const abs = Math.abs(value);
  let intPart: string;
  let fracPart: string;
  if (abs >= 1e21) {
    // toFixed switches to exponent notation here; such values have no meaningful fraction.
    intPart = BigInt(Math.round(abs)).toString();
    fracPart = '';
  } else {
    const [i = '0', f = ''] = abs.toFixed(maxFd).split('.');
    intPart = i;
    fracPart = f;
  }
  while (fracPart.length > minFd && fracPart.endsWith('0')) fracPart = fracPart.slice(0, -1);
  const isZero = /^0*$/.test(intPart) && /^0*$/.test(fracPart);
  const sign = negative && !isZero ? '-' : '';
  return `${sign}${groupIndian(intPart)}${fracPart ? `.${fracPart}` : ''}`;
}

// ---------------------------------------------------------------- dates

interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    partsFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/** Wall-clock fields of an instant in a timezone. */
export function getZonedParts(date: DateInput, timeZone: string = DEFAULT_TIME_ZONE): ZonedParts {
  const parts = partsFormatter(timeZone).formatToParts(toDate(date));
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`Intl.DateTimeFormat did not return a ${type} part`);
    return Number(part.value);
  };
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** `28 Sep 2026` — DD MMM YYYY in the given timezone (default Asia/Kolkata). */
export function formatDate(date: DateInput, timeZone: string = DEFAULT_TIME_ZONE): string {
  const p = getZonedParts(date, timeZone);
  return `${pad2(p.day)} ${MONTHS_SHORT[p.month - 1]} ${p.year}`;
}

export interface FormatTimeOptions {
  /** `2:05 PM` (default) or `14:05`. */
  hour12?: boolean;
}

/** `2:05 PM` (or `14:05` with `hour12: false`) in the given timezone. */
export function formatTime(
  date: DateInput,
  timeZone: string = DEFAULT_TIME_ZONE,
  options: FormatTimeOptions = {},
): string {
  const { hour12 = true } = options;
  const p = getZonedParts(date, timeZone);
  if (!hour12) return `${pad2(p.hour)}:${pad2(p.minute)}`;
  const suffix = p.hour < 12 ? 'AM' : 'PM';
  const hour = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${hour}:${pad2(p.minute)} ${suffix}`;
}

/** `28 Sep 2026, 2:05 PM` (or `28 Sep 2026, 14:05`) in the given timezone. */
export function formatDateTime(
  date: DateInput,
  timeZone: string = DEFAULT_TIME_ZONE,
  options: FormatTimeOptions = {},
): string {
  return `${formatDate(date, timeZone)}, ${formatTime(date, timeZone, options)}`;
}

/** Calendar date `YYYY-MM-DD` of an instant in the given timezone (API calendar dates, D-011). */
export function toISODateInTimeZone(date: DateInput, timeZone: string = DEFAULT_TIME_ZONE): string {
  const p = getZonedParts(date, timeZone);
  return `${String(p.year).padStart(4, '0')}-${pad2(p.month)}-${pad2(p.day)}`;
}
